/**
 * ImportPage — Settings → Import (Phase 20 / Task 20.7, `docs/CLAUDE.md` §80).
 *
 * §80's left nav: `+ New import`, Import history, Saved mapping profiles,
 * Templates.
 *
 * ── Who sees it ────────────────────────────────────────────────────────────
 * DEC-256 narrows §80: Tenant Admin and Dispatch Manager (plus Platform Admin),
 * NOT Dispatcher — the routes are all `MANAGE_HAULER_CATALOG`. The same role set
 * gates the entry point on the Settings page; a dispatcher who types the URL
 * gets an explanation here instead of a wizard whose every call would 403.
 */

import * as React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { HistoryPanel } from '../features/import/HistoryPanel';
import { ImportWizard } from '../features/import/ImportWizard';
import { ProfilesPanel } from '../features/import/ProfilesPanel';
import { canImportRates } from '../features/import/rate-tier';
import { TemplatesPanel } from '../features/import/TemplatesPanel';
import { IMPORT_ROLES } from '../features/import/roles';
import { Notice, TOUCH_MIN } from '../features/import/ui';
import { useExpressCustomer } from '../lib/express-customer';
import { confirmNavigation } from '../lib/navigation-guard';
import { useUserProfile } from '../lib/use-user-timezone';

type Section = 'new' | 'history' | 'profiles' | 'templates';

const SECTIONS: readonly { readonly id: Section; readonly label: string }[] = [
  { id: 'new', label: '+ New import' },
  { id: 'history', label: 'Import history' },
  { id: 'profiles', label: 'Saved mapping profiles' },
  { id: 'templates', label: 'Templates' },
];

export function ImportPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data: profile, isPending } = useUserProfile();
  const express = useExpressCustomer();
  const role = profile?.profile.role ?? null;
  const [section, setSection] = React.useState<Section>('new');
  // Bumped to remount the wizard when "+ New import" is clicked while on it.
  const [wizardKey, setWizardKey] = React.useState(0);

  const page: React.CSSProperties = {
    height: '100%',
    overflow: 'auto',
    padding: '24px',
    backgroundColor: 'var(--color-bg-primary)',
    color: 'var(--color-text-primary)',
    fontSize: '16px',
  };

  const back = (
    <button
      type="button"
      onClick={(): void => {
        if (!confirmNavigation()) return;
        void navigate('/settings');
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: TOUCH_MIN,
        fontSize: 16,
        color: 'var(--color-text-secondary)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      <ArrowLeft size={16} aria-hidden="true" /> Back to settings
    </button>
  );

  if (isPending) return <div style={page}>Loading…</div>;

  if (role === null || !IMPORT_ROLES.has(role)) {
    return (
      <div style={page}>
        {back}
        <div style={{ maxWidth: 720 }}>
          <Notice tone="warn" role="status">
            Importing data is available to tenant admins and dispatch managers. Ask one of them to
            run the import, or to change your role.
          </Notice>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      {back}
      <h1 style={{ fontSize: '22px', fontWeight: 600, margin: '4px 0 16px' }}>Import data</h1>
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <nav aria-label="Import" style={{ width: 240, flexShrink: 0 }}>
          <ul style={{ padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {SECTIONS.map((s) => {
              const active = section === s.id;
              return (
                <li key={s.id} style={{ listStyle: 'none' }}>
                  <button
                    type="button"
                    aria-current={active ? 'page' : undefined}
                    onClick={(): void => {
                      // From another section, return to the wizard as it was;
                      // from the wizard itself, start a fresh one.
                      // Starting a fresh one drops the running import's screen,
                      // so it is guarded like leaving the page. Switching to
                      // another section is not: the wizard is only hidden.
                      if (s.id === 'new' && section === 'new') {
                        if (!confirmNavigation()) return;
                        setWizardKey((k) => k + 1);
                      }
                      setSection(s.id);
                    }}
                    style={{
                      width: '100%',
                      minHeight: TOUCH_MIN,
                      textAlign: 'left',
                      padding: '0 16px',
                      fontSize: 16,
                      fontWeight: active ? 600 : 400,
                      borderRadius: 8,
                      border: '1px solid transparent',
                      cursor: 'pointer',
                      color: 'var(--color-text-primary)',
                      backgroundColor: active ? 'var(--color-bg-tertiary)' : 'transparent',
                    }}
                  >
                    {s.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        {/* A div, not <main>: the app shell already has the page's <main>. */}
        <div style={{ flex: '1 1 600px', minWidth: 0 }}>
          {/* Hidden, not unmounted: switching to Templates mid-import must not
              throw away the wizard's state. */}
          <div hidden={section !== 'new'}>
            {/* §58 PR 4 — on EXPRESS the preview rejects rows for other customers. */}
            {express !== null && (
              <p
                data-testid="import-express-note"
                style={{
                  fontSize: '13px',
                  color: 'var(--color-text-secondary)',
                  margin: '0 0 12px',
                }}
              >
                {`Your current plan covers one customer (${express.name}). Rows for other customers will be rejected.`}
              </p>
            )}
            <ImportWizard key={wizardKey} canImportRates={canImportRates(role)} />
          </div>
          {section === 'templates' && <TemplatesPanel />}
          {section === 'history' && <HistoryPanel />}
          {section === 'profiles' && <ProfilesPanel />}
        </div>
      </div>
    </div>
  );
}
